/**
 * PatientRoutes.jsx — patient dashboard routes.
 * Shaped by: ML1; owner 2026-10-12.
 * Invariants: `/` stays the Medical Library chat; `/medical-library` reuses that
 *   same ChatPage behind RequireLauncherApp; both chat paths are full width.
 * Related: GlobalAdminRoutes.jsx, appCatalog.js, ChatPage.tsx.
 */
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { useFacility } from "hooks/useFacility";
import UpgradePage from "pages/dashboard/UpgradePage";
import ApprovalPage from "pages/dashboard/global-admin/ApprovalPage";
import AssistantManagerPage from "pages/dashboard/global-admin/AssistantManagerPage";
import UsageMonitorPage from "pages/dashboard/global-admin/UsageMonitorPage";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import DashboardLayout from "components/dashboard/DashboardLayout";
import Settings from "components/dashboard/Settings";
import PatientOnboardingDialog from "components/dashboard/PatientOnboardingDialog";
import { MasterChartDashboardRoutes } from "routes/MasterChartDashboardRoutes";
import { isPatientOrP2LikeRole } from "shared/utils/constants/p2LikeRoles";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import MedicationsPage from "pages/dashboard/individual/MedicationsPage";
import MedReconciliationPage from "pages/dashboard/individual/MedReconciliationPage";
import EhrLinksPage from "pages/dashboard/individual/EhrLinksPage";
import PatientHomePage from "pages/dashboard/individual/PatientHomePage";
import MessagesPage from "pages/dashboard/individual/MessagesPage";
import DischargeInstructionsPage from "pages/dashboard/individual/DischargeInstructionsPage";
// TODO: MedicationTrackingPage disabled until Twilio BAA is in place (no reminders = no tracking utility)
// import MedicationTrackingPage from "pages/dashboard/individual/MedicationTrackingPage";
import { db } from "services/firebase";
import { PromptsManagerProvider } from "context/PromptsManager";
import CareGapsPage from "pages/dashboard/individual/CareGapsPage";
import ResultsCommsPage from "pages/dashboard/individual/ResultsCommsPage";
import RequireLauncherApp from "components/appLauncher/RequireLauncherApp";
import FeatureGate from "shared/components/FeatureGate";
import ChatPage from "components/chat_new/ChatPage";
import { PublicComputerIdleGuard } from "hooks/usePublicComputerIdleSignOut";

const PatientRoutes = () => {
  const { user, userData, subscription, isGlobalAdmin } = useAuth();
  const { data } = useFacility();
  const location = useLocation();
  const [showPatientOnboarding, setShowPatientOnboarding] = useState(false);

  // Full-width chat (minus sidebar): the thread must not sit inside the narrow page column.
  // The index and /medical-library both render ChatPage (ML1).
  const isPatientChatRoute = () => {
    const segments = location.pathname.replace(/\/$/, "").split("/").filter(Boolean);
    return (
      segments[0] === "dashboard" &&
      (segments.length === 1 ||
        (segments.length === 2 && segments[1] === "medical-library"))
    );
  };

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await db.collection("users").doc(user.uid).get();
          const userData = userDoc.data();

          if (
            isPatientOrP2LikeRole(userData?.role) &&
            !userData?.hideOnboardingDialog &&
            // Dev/mock patients skip the welcome overlay so MasterChart hub rows
            // (e.g. Demographics) stay clickable — same practical path as admin demos.
            !isDevAuthFirestoreUser(userData)
          ) {
            setShowPatientOnboarding(true);
          }
        } catch (error) {
          console.error("Error checking onboarding status:", error);
        }
      }
    };

    checkOnboardingStatus();
  }, [user]);

  return (
    <DashboardLayout fullWidth={isPatientChatRoute()}>
      <PublicComputerIdleGuard />
      <PatientOnboardingDialog
        open={showPatientOnboarding}
        onClose={() => setShowPatientOnboarding(false)}
        userId={user?.uid}
      />

      <PromptsManagerProvider>
        <Routes>
          <Route
            path="/"
            element={
              <FeatureGate feature="medical-library" showUpgradePrompt>
                <ChatPage assistantType="basic-medical-library" />
              </FeatureGate>
            }
          />
          <Route
            path="/medical-library"
            element={
              <RequireLauncherApp appId="medical-library">
                <FeatureGate feature="medical-library" showUpgradePrompt>
                  <ChatPage assistantType="basic-medical-library" />
                </FeatureGate>
              </RequireLauncherApp>
            }
          />

          <Route
            path="/settings"
            element={<Settings uid={user?.uid} subscription={subscription} />}
          />
          <Route path="/home" element={<PatientHomePage />} />
          {MasterChartDashboardRoutes()}
          <Route path="/medications" element={<MedicationsPage />} />
          <Route
            path="/med-reconciliation/:linkId"
            element={<MedReconciliationPage />}
          />
          <Route path="/linked-providers" element={<EhrLinksPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/discharge-instructions" element={<DischargeInstructionsPage />} />
          <Route
            path="/care-gaps"
            element={
              <RequireLauncherApp appId="care-gaps">
                <CareGapsPage />
              </RequireLauncherApp>
            }
          />
          <Route
            path="/results"
            element={
              <RequireLauncherApp appId="results">
                <ResultsCommsPage />
              </RequireLauncherApp>
            }
          />
          {/* TODO: Medication tracking disabled until Twilio BAA is in place */}
          {/* <Route
            path="/medication-tracking"
            element={<MedicationTrackingPage />}
          /> */}

          {!userData?.dailyPassExpiresAt && (
            <Route path="/upgrade" element={<UpgradePage />} />
          )}

          {/* Global-admin-only routes */}
          <Route
            path="/admin/approval"
            element={
              isGlobalAdmin ? (
                <ApprovalPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/assistants"
            element={
              isGlobalAdmin ? (
                <AssistantManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/usage"
            element={
              isGlobalAdmin ? (
                <UsageMonitorPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/prompts"
            element={
              isGlobalAdmin ? (
                <PromptsManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default PatientRoutes;
